import { generateId } from 'components/shared/AssetDelivery/utils'
import { Asset, AssetUpload } from './types'

interface AddFileUploadsAction {
  type: 'ADD_FILE_UPLOADS'
  files: {
    file: File
    asset: Asset
  }[]
}

interface MoveToGroupAction {
  type: 'MOVE_TO_GROUP'
  from: string
  to: string
}

interface RemoveFromGroupAction {
  type: 'REMOVE_FROM_GROUP'
  upload: AssetUpload
}

interface ShouldDeleteGroupAction {
  type: 'SHOULD_DELETE_GROUP'
  group: string
}

interface RemoveGroupAction {
  type: 'REMOVE_GROUP'
  group: string
}

interface UpdateData {
  shouldPrepare?: boolean
  isPreparing?: boolean
  shouldCreate?: boolean
  isCreating?: boolean
  shouldUpdate?: boolean
  isUpdating?: boolean
  shouldUpload?: boolean
  isUploading?: boolean
  progress?: number
  storedUrl?: string
  error?: Error
  asset?: Asset
}

interface UpdateUploadAction {
  type: 'UPDATE_UPLOAD'
  upload: AssetUpload
  data: UpdateData
}

interface UpdateUploadsAction {
  type: 'UPDATE_UPLOADS'
  data: {
    upload: AssetUpload
    data: UpdateData
  }[]
}

interface AddInitialAssetsAction {
  type: 'ADD_INITIAL_ASSETS'
  assets: Asset[]
}

interface RemoveUploadsAction {
  type: 'REMOVE_UPLOADS'
  ids: string[]
}

export type ReducerAction =
  | AddFileUploadsAction
  | MoveToGroupAction
  | RemoveFromGroupAction
  | ShouldDeleteGroupAction
  | RemoveGroupAction
  | UpdateUploadAction
  | UpdateUploadsAction
  | AddInitialAssetsAction
  | RemoveUploadsAction

function reducer(uploads: AssetUpload[], action: ReducerAction): AssetUpload[] {
  switch (action.type) {
    case 'ADD_FILE_UPLOADS': {
      return [
        ...uploads,
        ...action.files.map<AssetUpload>(({ file, asset }) => {
          const id = generateId()
          return {
            id,
            uploadUrl: null,
            md5: null,
            progress: 0,
            file,
            asset: {
              id: null,
              isPrimary: true,
              labels: [],
              groupId: id,
              masterForGroupId: id,
              comment: null,
              ...asset,
            },
            shouldPrepare: true,
            isPreparing: false,
            shouldCreate: false,
            isCreating: false,
            shouldUpdate: false,
            isUpdating: false,
            shouldUpload: false,
            isUploading: false,
            shouldDelete: false,
            isDeleting: false,
          }
        }),
      ]
    }

    case 'MOVE_TO_GROUP': {
      const masterForGroupAsset = uploads.find(
        (u) => u.asset.masterForGroupId === action.to
      )?.asset

      return uploads.map((upload) => {
        if (upload.asset.groupId === action.from && masterForGroupAsset) {
          return {
            ...upload,
            shouldUpdate: true,
            asset: {
              ...upload.asset,
              groupId: action.to,
              masterForGroupId: null,
              isPrimary: false,
              labels: masterForGroupAsset.labels,
            },
          }
        }
        return upload
      })
    }

    case 'REMOVE_FROM_GROUP': {
      return uploads.map((upload) => {
        if (upload.id === action.upload.id) {
          const groupId = generateId()
          return {
            ...upload,
            shouldUpdate: true,
            asset: {
              ...upload.asset,
              groupId,
              masterForGroupId: groupId,
              isPrimary: false,
            },
          }
        }
        return upload
      })
    }

    case 'SHOULD_DELETE_GROUP': {
      return uploads.map((upload) => {
        if (upload.asset.groupId === action.group) {
          return { ...upload, shouldDelete: true }
        }
        return upload
      })
    }

    case 'REMOVE_GROUP': {
      return uploads.filter((upload) => upload.asset.groupId !== action.group)
    }

    case 'UPDATE_UPLOAD': {
      return uploads.map((upload) => {
        if (upload.id === action.upload.id) {
          return {
            ...upload,
            shouldUpdate: !!(upload.asset.id && action.data.asset),
            ...action.data,
            asset: {
              ...upload.asset,
              ...(action.data.asset || {}),
            },
          }
        }
        return upload
      })
    }

    case 'UPDATE_UPLOADS': {
      return uploads.map((upload) => {
        for (const input of action.data) {
          if (input.upload.id === upload.id) {
            return {
              ...upload,
              shouldUpdate: !!(upload.asset.id && input.data.asset),
              ...input.data,
              asset: {
                ...upload.asset,
                ...(input.data.asset || {}),
              },
            }
          }
        }
        return upload
      })
    }

    case 'ADD_INITIAL_ASSETS': {
      return [
        ...uploads,
        ...action.assets.map<AssetUpload>((asset) => ({
          id: generateId(),
          progress: null,
          file: null,
          uploadUrl: null,
          md5: null,
          asset: {
            ...asset,
            groupId: asset.groupId || generateId(),
          },
          shouldPrepare: false,
          isPreparing: false,
          shouldCreate: false,
          isCreating: false,
          shouldUpdate: false,
          isUpdating: false,
          shouldUpload: false,
          isUploading: false,
          shouldDelete: false,
          isDeleting: false,
        })),
      ]
    }

    case 'REMOVE_UPLOADS': {
      return uploads.filter((upload) => upload.asset.id && !action.ids.includes(upload.asset.id))
    }

    default:
      throw new Error(`Unknown action ${JSON.stringify(action)}`)
  }
}

export default reducer
